import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Activity, TrendingDown, TrendingUp, Zap, Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CircularProgress from "@/components/CircularProgress";
import BadgeList from "@/components/BadgeList";
import StreakCard from "@/components/StreakCard";
import { exportEmissionsToCSV, exportEmissionsReportToPDF } from "@/lib/exports";
import { toast } from "sonner";
import SectorComparison from "@/components/SectorComparison";
import RecommendationsPanel from "@/components/RecommendationsPanel";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * MyPA Dashboard Page
 * Overview of carbon emissions with visualizations
 */
export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Fetch real data from tRPC
  const { data: periods, isLoading: loadingPeriods } = trpc.periods.list.useQuery();
  
  // Period selector state
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>("");
  
  // Auto-select first period when loaded
  const currentPeriod = selectedPeriodId 
    ? periods?.find(p => p.id === selectedPeriodId)
    : periods?.[0];
  
  // Update selected period when periods load
  if (periods && periods.length > 0 && !selectedPeriodId) {
    setSelectedPeriodId(periods[0].id);
  }
  
  const { data: emissions, isLoading: loadingEmissions } = trpc.calculator.listByPeriod.useQuery(
    { periodId: currentPeriod?.id || "" },
    { enabled: !!currentPeriod?.id }
  );
  
  const { data: progress, isLoading: loadingProgress } = trpc.learn.getMyProgress.useQuery();
  
  const { data: diagnosticData } = trpc.calculator.diagnostic.useQuery();
  
  const isLoading = loadingPeriods || loadingEmissions || loadingProgress;
  
  // Calculate total emissions from real data
  const totalEmissions = emissions?.reduce((sum, e) => sum + e.tco2e, 0) || 0;
  
  // Calculate scope breakdown from real data
  // Note: scope is flattened by getEmissionEntries (entry.scope, not entry.fe_master.scope)
  const scopeBreakdown = emissions?.reduce((acc, e: any) => {
    const rawScope = e.scope || e.fe_master?.scope || 'Scope 3';
    // Normalize scope values: 'Scope 1/3' split equally between Scope 1 and Scope 3
    if (rawScope === 'Scope 1/3') {
      acc['Scope 1'] = (acc['Scope 1'] || 0) + e.tco2e / 2;
      acc['Scope 3'] = (acc['Scope 3'] || 0) + e.tco2e / 2;
    } else {
      acc[rawScope] = (acc[rawScope] || 0) + e.tco2e;
    }
    return acc;
  }, {} as Record<string, number>) || {};
  
  const scope1 = scopeBreakdown['Scope 1'] || 0;
  const scope2 = scopeBreakdown['Scope 2'] || 0;
  const scope3 = scopeBreakdown['Scope 3'] || 0;
  
  // Monthly change - TODO: Calculate from historical periods when available
  const monthlyChange = 0; // Placeholder until we have historical data

  // Monthly data - TODO: Get from multiple periods when available
  const monthlyData = [
    { month: "Current", emissions: totalEmissions },
  ];

  const scopeData = [
    { name: "Scope 1", value: scope1, color: "#1e3a5f" },
    { name: "Scope 2", value: scope2, color: "#4a9d7e" },
    { name: "Scope 3", value: scope3, color: "#a5b4fc" },
  ].filter(s => s.value > 0); // Only show scopes with data

  // Category breakdown from real data
  // Note: category is flattened by getEmissionEntries (entry.category, not entry.fe_master.category)
  const categoryBreakdown = emissions?.reduce((acc, e: any) => {
    const category = e.category || e.fe_master?.category || 'Other';
    acc[category] = (acc[category] || 0) + e.tco2e;
    return acc;
  }, {} as Record<string, number>) || {};
  
  const categoryData = Object.entries(categoryBreakdown)
    .map(([category, emissions]) => ({ category, emissions }))
    .sort((a, b) => b.emissions - a.emissions);

  // Calculate inventory completion percentage
  const totalExpectedFE = diagnosticData?.totalMappingEntries || 1; // FE attendus
  // Count unique FE used (not total entries)
  const uniqueFEKeys = new Set(emissions?.map(e => e.fe_key).filter(Boolean));
  const totalEnteredFE = uniqueFEKeys.size; // FE uniques saisis
  const inventoryCompletionPercentage = Math.min(100, Math.round((totalEnteredFE / totalExpectedFE) * 100));
  
  // Gamification data from real progress (for learning modules)
  const totalModules = (progress || []).length || 1;
  const completedModules = (progress || []).filter(p => p.quiz_completed).length;
  const learningCompletionPercentage = Math.round((completedModules / totalModules) * 100);
  
  // Activity streak (simplified - would need user_progress table with dates)
  const activityStreak = completedModules > 0 ? Math.min(completedModules, 7) : 0;

  const encouragementMessage = inventoryCompletionPercentage >= 50
    ? t('dashboard.encouragement_halfway')
    : t('dashboard.encouragement_start');

  // Export handlers
  const handleExportCSV = () => {
    if (!emissions || emissions.length === 0) {
      toast.error(t('dashboard.no_emissions_to_export'));
      return;
    }
    exportEmissionsToCSV(emissions, currentPeriod?.period_label || 'current');
    toast.success(t('dashboard.csv_exported_successfully'));
  };

  const handleExportPDF = () => {
    if (!emissions || emissions.length === 0) {
      toast.error(t('dashboard.no_emissions_to_export'));
      return;
    }
    exportEmissionsReportToPDF(
      emissions,
      totalEmissions,
      currentPeriod?.period_label || 'Current Period',
      scope1,
      scope2,
      scope3
    );
    toast.success(t('dashboard.pdf_report_generated'));
  };

  // Sector comparison data
  const sectorAverageEmissions = 1400;

  // AI Recommendations - now powered by RecommendationsPanel component

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with Welcome and Period Selector */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t('dashboard.welcome', { name: user?.firstName || user?.email?.split("@")[0] || "User" })}
            </h1>
            <p className="text-muted-foreground">
              {t('dashboard.progress', { percentage: inventoryCompletionPercentage })}
            </p>
          </div>
          
          {/* Period Selector */}
          {periods && periods.length > 0 && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-muted-foreground">{t('dashboard.period')}</label>
              <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
                <SelectTrigger className="w-[250px]">
                  <SelectValue placeholder={t('dashboard.selectPeriod')} />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period.id} value={period.id}>
                      {period.label || period.period_label || `${period.year} ${period.month || ''}`.trim()}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({period.status === 'closed' ? t('common.closed') : t('common.open')})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        
        {/* Loading State */}
        {isLoading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-3 text-muted-foreground">{t('dashboard.loading_data')}...</span>
            </CardContent>
          </Card>
        )}
        
        {!isLoading && (
          <>
            {/* Progress Bar with Encouragement */}
            <Card className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <CircularProgress percentage={inventoryCompletionPercentage} size={80} strokeWidth={6} />
                <div>
                  <p className="text-lg font-semibold">{encouragementMessage}</p>
                  <p className="text-sm opacity-90">{t('dashboard.overall_progress')}</p>
                </div>
              </div>
              <div className="text-5xl font-bold">{inventoryCompletionPercentage}%</div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards with Streak */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="md:col-span-2 lg:col-span-3">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t('dashboard.totalEmissions')}
              </CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalEmissions.toFixed(1)} tCO₂e</div>
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                {monthlyChange < 0 ? (
                  <>
                    <TrendingDown className="h-3 w-3 text-green-500" />
                    <span className="text-green-500">{Math.abs(monthlyChange)}%</span>
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-3 w-3 text-red-500" />
                    <span className="text-red-500">+{monthlyChange}%</span>
                  </>
                )}
                <span>{t('dashboard.vs_last_month')}</span>
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.scope1')}</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scope1.toFixed(1)} tCO₂e</div>
              <p className="text-xs text-muted-foreground">
                {((scope1 / totalEmissions) * 100).toFixed(1)}% {t('dashboard.of_total')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.scope2')}</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scope2.toFixed(1)} tCO₂e</div>
              <p className="text-xs text-muted-foreground">
                {((scope2 / totalEmissions) * 100).toFixed(1)}% {t('dashboard.of_total')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('dashboard.scope3')}</CardTitle>
              <Zap className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{scope3.toFixed(1)} tCO₂e</div>
              <p className="text-xs text-muted-foreground">
                {((scope3 / totalEmissions) * 100).toFixed(1)}% {t('dashboard.of_total')}
              </p>
            </CardContent>
          </Card>
            </div>
          </div>
          
          {/* Activity Streak Card */}
          <div className="md:col-span-2 lg:col-span-1">
            <StreakCard days={activityStreak} />
          </div>
        </div>

        {/* Badges Section */}
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-4">{t('dashboard.your_badges')} 🏆</h2>
          <BadgeList />
        </div>

        {/* Sector Comparison */}
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-4">{t('dashboard.key_insights')} 📊</h2>
          <SectorComparison
            yourEmissions={totalEmissions}
            averageEmissions={sectorAverageEmissions}
            sector="Manufacturing"
          />
        </div>

        {/* AI Recommendations - Dynamic Panel */}
        <RecommendationsPanel periodId={currentPeriod?.id} compact />

        {/* Charts */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Monthly Trend */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('dashboard.monthly_emissions_trend')}</CardTitle>
                  <CardDescription>
                    {t('dashboard.monthly_emissions_trend_desc')}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_pdf')}
                    onClick={handleExportPDF}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_csv')}
                    onClick={handleExportCSV}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorEmissions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4a9d7e" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#4a9d7e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="emissions"
                    stroke="#4a9d7e"
                    fillOpacity={1}
                    fill="url(#colorEmissions)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Scope Distribution */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('dashboard.emissions_by_scope')}</CardTitle>
                  <CardDescription>
                    {t('dashboard.emissions_by_scope_desc')}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_pdf')}
                    onClick={handleExportPDF}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_csv')}
                    onClick={handleExportCSV}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={scopeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {scopeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{t('dashboard.emissions_by_category')}</CardTitle>
                  <CardDescription>
                    {t('dashboard.emissions_by_category_desc')}
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_pdf')}
                    onClick={handleExportPDF}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    title={t('common.export_as_csv')}
                    onClick={handleExportCSV}
                    disabled={!emissions || emissions.length === 0}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="emissions" fill="#1e3a5f" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
        </>
        )}
      </div>
    </DashboardLayout>
  );
}

